import { ThreeDotsIcon, MenuItemProps, FaroIconButtonSizes, IconButton, Menu, MenuItem, MenuSeparator } from "@flat-ui";
import React, { useState } from "react";

export interface ActionButtonMenuItem
  extends Pick<MenuItemProps, "label" | "color" | "highlightColor" | "Icon"> {
  /** Click handler for the menu item */
  onClick?: () => void;

  /** Flag to determine if the menu item should be visible */
  isVisible: boolean;

  /** Type of the menu item, can be "separator" for visual separation */
  type?: "separator";
}

interface Props {
  /** Array of menu items to be displayed */
  menuItems: Array<ActionButtonMenuItem>;

  /** Size of the icon button */
  size?: FaroIconButtonSizes;
}

/**
 * Displays a button with a vertical dots icon.
 * When clicked, it opens a menu with various actions.
 * @param {Object} props - The properties for the VerticalDotsMenu component.
 * @returns {JSX.Element | null} - Returns a JSX element representing the button and menu, or null if no items are provided.
 */
export function VerticalDotsMenu({
  menuItems,
  size,
}: Props): JSX.Element | null {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  if (!menuItems || menuItems.length === 0) {
    return null;
  }

  /** Handler for menu item click events. */
  function onClickHandler(itemOnClick?: () => void) {
    return () => {
      setIsMenuOpen(false);
      itemOnClick?.();
    };
  };

  return (
    <>
      <IconButton
        ref={anchorRef}
        size={size}
        onClick={() => setIsMenuOpen(true)}
        sx={{
          marginLeft: "0.25rem",
        }}
      >
        <ThreeDotsIcon />
      </IconButton>
      <Menu
        open={isMenuOpen}
        anchorEl={anchorRef.current}
        onClose={() => setIsMenuOpen(false)}
        minWidth="150px"
      >
        {menuItems
          .filter((menuItem) => menuItem.isVisible)
          .map((item, index) => {
            if (item.type === "separator") {
              return <MenuSeparator key={index} />;
            }
            return (
              <MenuItem
                key={index}
                label={item.label}
                onClick={onClickHandler(item.onClick)}
                Icon={item.Icon}
                color={item.color}
                highlightColor={item.highlightColor}
              />
            );
          })}
      </Menu>
    </>
  );
}